import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { isBooleanTrue, isObjectNotEmpty } from '../utils/objectUtils';
import User from '../models/User';
import { subscribeToFCMTopic } from '../firebase/Messaging';

class NotificationService {
    static vapidKey = process.env.REACT_APP_FIREBASE_CLOUD_MESSAGING_VAP_KEY;
    static messaging = null;

    static isInAppBrowser() {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        const isAndroidTikTok = /TikTok/i.test(ua) || /tiktok/i.test(ua) || /com\.zhiliaoapp\.musically/i.test(ua);

        const isFacebook = /FBAN|FBAV/i.test(ua);
        const isInstagram = /Instagram/i.test(ua);
        const isTikTok = /TikTok/i.test(ua) || /com\.zhiliaoapp\.musically/i.test(ua);
        const isTwitter = /Twitter/i.test(ua);
        const isSnapchat = /Snapchat/i.test(ua);
        const isMessenger = /Messenger/i.test(ua);
        const isPinterest = /Pinterest/i.test(ua);
        const isNewsBreak = /NewsBreak|newsbreak/i.test(ua);
        const isGroupon = /Groupon/i.test(ua);
        const isReddit = /Reddit/i.test(ua);
        const isLinkedIn = /LinkedIn/i.test(ua);
        const isGmail = /Gmail|GoogleImageProxy/i.test(ua);
        const isOutlook = /Outlook/i.test(ua);
        const isYahoo = /Yahoo/i.test(ua);
        const isDiscord = /Discord/i.test(ua);
        const isSlack = /Slack/i.test(ua);
        const isTelegram = /Telegram/i.test(ua);
        const isViber = /Viber/i.test(ua);
        const isWeChat = /MicroMessenger/i.test(ua);
        const isLine = /Line/i.test(ua);

        // iOS TikTok — heuristic detection
        const isIos = /iPhone|iPad|iPod/i.test(ua);
        const isWebKit = /AppleWebKit/i.test(ua);
        const isNoSafari = !/Safari/i.test(ua); // TikTok uses WebKit but often not full Safari
        const isIosLikelyTikTok = isIos && isWebKit && isNoSafari;

        // --- Feature detection fallback (TikTok often lacks PushManager or ServiceWorker)
        const lacksPush = !('PushManager' in window);
        const lacksSW = !('serviceWorker' in navigator);

        return (
            ua.includes('FBAN') || // Facebook
            ua.includes('FBAV') || // Facebook
            ua.includes('Instagram') ||
            ua.includes('Line') ||
            ua.includes('Snapchat') ||
            ua.includes('Messenger') ||
            ua.includes('Pinterest') ||
            ua.includes('Twitter') ||
            ua.includes('News_Break') ||
            ua.includes('NewsBreak') ||
            ua.includes('newsbreak') ||
            ua.includes('Groupon') ||
            ua.includes('Reddit') ||
            ua.includes('redditapp') ||
            ua.includes('LinkedIn') ||
            ua.includes('Gmail') ||
            ua.includes('GoogleImageProxy') ||
            ua.includes('Outlook-iOS') ||
            ua.includes('Outlook-Android') ||
            ua.includes('YahooMobile') ||
            ua.includes('Yahoo') ||
            ua.includes('Discord') ||
            ua.includes('Slack') ||
            ua.includes('Telegram') ||
            ua.includes('Viber') ||
            ua.includes('MicroMessenger') ||
            ua.includes('TikTok') ||
            ua.includes('com.zhiliaoapp.musically') ||
            isAndroidTikTok ||
            isIosLikelyTikTok ||
            isFacebook ||
            isInstagram ||
            isTikTok ||
            isTwitter ||
            isSnapchat ||
            isMessenger ||
            isPinterest ||
            isNewsBreak ||
            isGroupon ||
            isReddit ||
            isLinkedIn ||
            isGmail ||
            isOutlook ||
            isYahoo ||
            isDiscord ||
            isSlack ||
            isTelegram ||
            isViber ||
            isWeChat ||
            isLine ||
            lacksPush ||
            lacksSW
        );
    }

    static async initialize() {
        // Don't initialize if in an in-app browser
        if (this.isInAppBrowser()) {
            console.log('In-app browser detected, skipping FCM initialization');
            return false;
        }

        try {
            if (!this.messaging) {
                this.messaging = getMessaging();
            }
            return true;
        } catch (error) {
            console.error('Error initializing FCM:', error);
            return false;
        }
    }

    static async requestPermission(userId) {
        if (!this.messaging) {
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            console.log('permission', permission);
            if (permission === 'granted') {
                let serviceWorkerRegistration;
                if ('serviceWorker' in navigator) {
                    const swUrl = `/firebase-messaging-sw.js?apiKey=${process.env.REACT_APP_FIREBASE_API_KEY}&projectId=${process.env.REACT_APP_FIREBASE_PROJECT_ID}&messagingSenderId=${process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID}&appId=${process.env.REACT_APP_FIREBASE_APP_ID}&measurementId=${process.env.REACT_APP_FIREBASE_MEASUREMENT_ID}`;
                    serviceWorkerRegistration = await navigator.serviceWorker.register(swUrl);
                }

                const token = await getToken(this.messaging, {
                    vapidKey: this.vapidKey,
                    serviceWorkerRegistration
                });

                if (token) {
                    console.log('token', token);
                    // Save the token to the user's device IDs
                    await this.saveDeviceToken(userId, token);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    static async saveDeviceToken(userId, token) {
        try {
            const user = await User.getById(userId);
            if (isObjectNotEmpty(user)) {
                const deviceTokens = user.deviceTokens || [];
                if (!deviceTokens.includes(token)) {
                    deviceTokens.push(token);
                    const success = await User.updateFields(userId, { deviceTokens });
                    if (success) {
                        if (isBooleanTrue(user.isRep) || isBooleanTrue(user.isStaffManager) || isBooleanTrue(user.isMarketingManager) || isBooleanTrue(user.isOwner) || isBooleanTrue(user.isAdmin) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'allstaff');
                        }
                        if (isBooleanTrue(user.isRep) || isBooleanTrue(user.isStaffManager) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'reps');
                        }
                        if (isBooleanTrue(user.isAdmin) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'admins');
                        }
                        if (isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'superadmins');
                        }
                        if (isBooleanTrue(user.isStaffManager) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'staffmanagers');
                        }
                        if (isBooleanTrue(user.isOwner) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'owners');
                        }
                        if (isBooleanTrue(user.isHelpStaff) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'helpstaff');
                        }
                        if (isBooleanTrue(user.isMarketingManager) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'marketingmanagers');
                        }
                        if (isBooleanTrue(user.isMarketingManager) || isBooleanTrue(user.isAdmin) || isBooleanTrue(user.isSuperAdmin)) {
                            await subscribeToFCMTopic(token, 'corpstaff');
                        }

                        console.log('Saved device tokenId');
                    }
                }
            }
        } catch (error) {
            console.error('Error saving device token:', error);
        }
    }

    static onMessageListener() {
        return new Promise((resolve) => {
            onMessage(this.messaging, (payload) => {
                resolve(payload);
            });
        });
    }
}

export default NotificationService;